import { AccountFinal } from '@/api/entities';
import { normalizeTenantId } from '@/lib/tenantUtils';
import { isNotInPilotError } from '@/lib/pilotMode';

/**
 * Check if a string looks like a UUID
 */
const looksLikeUUID = (s) => typeof s === 'string' && /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(s);

/**
 * Build tenant filter for AccountFinal/PersonFinal queries (same logic as Market.jsx)
 */
export function buildTenantFilter(tenantId) {
  const tid = normalizeTenantId(tenantId);
  if (!tid) return { $in: ['instructure', 'default', 'global'] };
  if (tid === 'instructure') {
    return { $in: ['instructure', 'clairelabs', 'default', 'global'] };
  } else if (tid === 'smowl') {
    return { $in: ['smowl'] };
  } else if (tid === 'diambra') {
    return { $in: ['diambra', 'smowl', 'instructure', 'clairelabs', 'base44', 'default', 'global'] };
  } else if (tid === 'clairelabs') {
    return { $in: ['clairelabs', 'default', 'global'] };
  } else if (tid === 'faria' || tid === 'edqwest') {
    return { $in: ['faria', 'edqwest', 'default', 'global'] };
  } else if (tid === 'calmedica') {
    return { $in: [tid] };
  } else if (tid === 'sebit') {
    return { $in: [tid, 'global'] };
  } else if (tid === 'plataformaa') {
    return { $in: [tid, 'global'] };
  } else if (tid === 'learnwise') {
    return { $in: [tid, 'global'] };
  } else {
    return { $in: [tid, 'default', 'global'] };
  }
}

/**
 * Resolve an account by id or account_id with tenant isolation
 */
export async function resolveAccount(identifier, tenantId) {
  if (!identifier) return null;
  if (!tenantId) {
    console.warn('⚠️ resolveAccount: tenantId required for tenant isolation');
    tenantId = 'instructure'; // Fallback
  }
  
  console.log('🔍 resolveAccount: Looking for', identifier, 'tenant:', tenantId);
  
  const tenantFilter = buildTenantFilter(normalizeTenantId(tenantId) || tenantId);
  
  // For UUIDs, try both id and account_id fields
  if (looksLikeUUID(identifier)) {
    // Try by id (primary key) first
    try {
      console.log('🔍 resolveAccount: Trying id filter...');
      const byId = await AccountFinal.filter({ id: identifier, tenant_id: tenantFilter }, "-account_fit_score", 1);
      console.log('🔍 resolveAccount: id result count:', byId?.length || 0);
      if (byId && byId.length > 0) {
        console.log('✅ resolveAccount: Found by id');
        return byId[0];
      }
    } catch (error) {
      if (isNotInPilotError(error)) throw error;
      console.warn('⚠️ resolveAccount: id lookup failed:', error.message);
    }

    // Try by account_id field
    try {
      console.log('🔍 resolveAccount: Trying account_id filter...');
      const byAccountId = await AccountFinal.filter({ account_id: identifier, tenant_id: tenantFilter }, "-account_fit_score", 1);
      console.log('🔍 resolveAccount: account_id result count:', byAccountId?.length || 0);
      if (byAccountId && byAccountId.length > 0) {
        console.log('✅ resolveAccount: Found by account_id');
        return byAccountId[0];
      }
    } catch (error) {
      if (isNotInPilotError(error)) throw error;
      console.warn('⚠️ resolveAccount: account_id lookup failed:', error.message);
    }
  }
  
  // For non-UUIDs, try account_id first (e.g., 'australia_001_006'), then account_name
  if (!looksLikeUUID(identifier)) {
    // Try by account_id field first (for custom account_id strings)
    try {
      console.log('🔍 resolveAccount: Trying account_id filter...');
      const byAccountId = await AccountFinal.filter({ account_id: identifier, tenant_id: tenantFilter }, "-account_fit_score", 1);
      if (byAccountId && byAccountId.length > 0) {
        console.log('✅ resolveAccount: Found by account_id');
        return byAccountId[0];
      }
    } catch (error) {
      if (isNotInPilotError(error)) throw error;
      console.warn('⚠️ resolveAccount: account_id lookup failed:', error.message);
    }
    
    // Fallback: try account_name (with tenant filter for safety)
    try {
      console.log('🔍 resolveAccount: Trying account_name filter...');
      const byName = await AccountFinal.filter({ account_name: identifier, tenant_id: tenantFilter }, "-account_fit_score", 1);
      if (byName && byName.length > 0) {
        console.log('✅ resolveAccount: Found by account_name');
        return byName[0];
      }
    } catch (error) {
      if (isNotInPilotError(error)) throw error;
      console.warn('⚠️ resolveAccount: account_name lookup failed:', error.message);
    }
  }
  
  console.warn(`⚠️ resolveAccount: No account found for '${identifier}' in tenant '${tenantId}'`);
  return null;
}